import { useState, useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useSignInState } from '../Components/signInState';
import { getErrorMessage } from '../Helpers/utils';

interface useFetchProps {
  scope: string;
}

interface FetchAPIProps {
  url: string;
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | undefined;
  body?: Record<string, unknown>;
}

interface UseFetchReturn {
  fetchAPI: (props: FetchAPIProps) => Promise<void>;
  isLoading: boolean;
  serverError: Error | null;
  apiData: Record<string, string | number | boolean | undefined> | null;
}

type ReqHeaders = { 
  "Content-Type": string;
  "Authorization": string
}

const useFetch = ({ scope }: useFetchProps): UseFetchReturn => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { accounts, instance, inProgress } = useMsal();
  const { signInState } = useSignInState();

  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<Error | null>(null);
  const [apiData, setApiData] = useState<Record<string, string | number | boolean | undefined> | null>(null);

  const getAuthHeader = useCallback(async (): Promise<{headers: ReqHeaders}> => {
    let currentCustomerTid = signInState.customerInfo.find((customer: { CustomerID: number; }) => customer.CustomerID === signInState.currentCustomerID)?.TenantID
    if (inProgress !== 'none' && !accounts.length) {
      // Wait for 1 second before trying again
      await new Promise(resolve => setTimeout(resolve, 1000));
      return getAuthHeader();
    }
    let currentAccount = accounts.find(account => account.tenantId === currentCustomerTid);
    if (!currentAccount && accounts.length) {
      currentCustomerTid = currentCustomerTid ||accounts[0].tenantId;
      currentAccount = accounts[0];
    }

    const acquireTokenPayload = {
      scopes: [`api://${process.env.REACT_APP_CLIENT_APP_ID}/${scope}`],
      authority: `https://login.microsoftonline.com/${currentAccount?.tenantId}`,
      account: currentAccount,
    };

    const baseHeaders: ReqHeaders = {
      "Content-Type": "application/json",
      "Authorization": "",
      ...(currentCustomerTid && { "x-customertid": currentCustomerTid }),
    }

    console.log('[useFetchAPI] baseHeaders:', baseHeaders, signInState);

    try {
      const silentResult = await instance.acquireTokenSilent(acquireTokenPayload);
      return {
        headers: {
          ...baseHeaders,
          "Authorization": `Bearer ${silentResult.accessToken}`,
        },
      };
    } catch (error) {
      if ((error as any).name === "InteractionRequiredAuthError") {
        const interactiveResult = await instance.acquireTokenPopup(acquireTokenPayload);
        return {
          headers: {
            ...baseHeaders,
            "Authorization": `Bearer ${interactiveResult.accessToken}`,
          },
        };
      } else {
        throw error;
      }
    }
  }, [signInState]);

  const fetchAPI = useCallback(async ({ url, method, body }: FetchAPIProps) => {
    setIsLoading(true);
    try {
      const header = await getAuthHeader();
      console.log('[useFetchAPI] header:', header);
      const fullUrl = url.startsWith('http') ? url : `${apiUrl}${url}`;
      const response = await fetch(fullUrl, {
        // ...header,
        headers: header.headers,
        method: method,
        ...(body && { body: JSON.stringify(body) })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(getErrorMessage(errorText));
      }

      const data = await response.json();
      setApiData(data);
    } catch (error) {
      if (error instanceof Error) {
        setServerError(error);
      } else {
        setServerError(new Error('An unknown error occurred'));
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, getAuthHeader, signInState]);

  return { fetchAPI, isLoading, serverError, apiData };
};

export default useFetch;